import {
    changeDateFormat,
    getMetricTimeStamp,
} from "../../../Helper/DatePicker/DateConverter";
import {
    formatCellForCSV,
    getDefaultSelectedColumns,
} from "../Network/tableHelpers";
import { selectedIDs, selectedValues } from "../filterHelpers";
import { statusTheme } from "../statusPalette";

// Pure helpers behind the Assets list, its filters, its detail view and its
// CSV export. Kept out of the components and exported so they can be unit
// tested directly, per the repo's "test the pure function, don't mount the
// page" rule.
//
// WHY THIS FILE EXISTS AT ALL, rather than importing Network/tableHelpers.js
// wholesale. The two tabs share the table component but not its column
// vocabulary: assetsTable declares `date`, `date-time`, `status-chip`,
// `text-with-copy` and `actions`, none of which Network's formatCellForCSV
// knows, and its getDefaultSelectedColumns excludes only `action-menu`, so the
// Actions column would arrive in the picker as a selectable "column".
//
// What IS reused is imported rather than re-implemented: formatCellForCSV for
// every type both tabs share, and getDefaultSelectedColumns for the
// defaultVisible / dataIndex rules, which are a property of the table
// component and not of either tab.
//
// WHERE THE CSV IS BUILT. The server builds it: /assets/list-action renames
// the columns and localises the dates (convertDataForCSV over
// _serialiseExportRow, which keeps raw datetimes for exactly that purpose),
// and it does so identically for the download and for the mail attachment, so
// the two are the same file by construction. The client's remaining job is
// small and listed on buildAssetCSVRows.
//
// The consequence, and it is the whole reason these date formats are what they
// are: the CELL has to render what the SERVER writes. formatAssetCell below
// reproduces convert_non_serializable's output character for character, and
// the parity test asserts that against literal expected strings.

// Column types that are controls, not data. They are never offered in the
// column picker and never written to the CSV. Both spellings are listed
// because the two tabs' configs disagree: Network's actions column is
// `action-menu`, the assets one is `actions`.
// One name, matching the six network tables and the platform helper at
// GenericDraggableTableFuncations.js:21. This was briefly ["actions",
// "action-menu"] because the two new tables declared a name of their own --
// two spellings for one concept inside one page, which is how a column ends up
// recognised by the column picker but not by the drag handler.
export const ACTION_COLUMN_TYPES = ["action-menu"];

export const isActionColumn = (column) =>
    ACTION_COLUMN_TYPES.includes(column?.valueRenderingType);

// --------------------------------------------------------------------------
// Badge themes
// --------------------------------------------------------------------------
//
// ONE RAMP, defined in statusPalette.js and imported here. This table used to
// carry its own copy -- a green, an amber, a red and a blue that were each a
// shade off the ones the work order chips and the serial verdicts used, for
// the same four meanings. See the note at the top of that file.
//
// The alias table and the neutral fallback live there too, because an org's
// badgeColorTheme is free text and every consumer of it needs the same
// forgiveness.
export const getBadgeTheme = statusTheme;

// --------------------------------------------------------------------------
// The stateless population
// --------------------------------------------------------------------------

// Not a state code. The list endpoint accepts this key and turns it into
// `currentStateCode IS NULL` -- assets registered outside a work order have no
// lifecycle row and therefore no current state at all. The same constant lives
// in asset_db.py, so the filtering cannot drift across the wire.
export const UNASSIGNED_STATE_KEY = "__unassigned__";
export const UNASSIGNED_STATE_LABEL = "Not yet moved";

// --------------------------------------------------------------------------
// Narrowing the list
// --------------------------------------------------------------------------

// The keys the assetsTable panel declares. Named so the mapping and the tests
// all mean the same string.
export const ASSET_FILTER_KEY_TYPE = "assetTypeCode";
export const ASSET_FILTER_KEY_STATE = "stateCode";
export const ASSET_FILTER_KEY_ENTITY = "currentEntityID";
// Who SUPPLIED the asset, as opposed to ASSET_FILTER_KEY_ENTITY's current
// holder. Limiting its options to vendors is the pageMeta declaration's job
// (queryParams.queryFilters.entityTypeCode), not this file's.
export const ASSET_FILTER_KEY_VENDOR = "sourceVendorEntityID";

// Panel selection -> request params.
//
// ONE mapping, fed to BOTH places the narrowing has to reach: the list and the
// list-action export. A filter applied to the table but not to the export
// gives a download that disagrees with the screen. The backend declares them
// in one mixin and applies them through one builder for exactly this reason --
// this is the client half of that contract.
//
// ARRAYS, whole. Every field takes one or many values under the same param
// name and the query layer uses IN, so nothing the user ticked is dropped. The
// encoding -- comma separated on a query string, a real array in a POST body --
// belongs to the transport and is applied once, in buildAssetParams.
//
// Nothing selected produces NO params at all, rather than keys set to
// undefined or to an empty list: the schemas are unknown = RAISE, qs would
// serialise `stateCode=` , and an empty IN () reads to a user as "this filter
// found no rows" rather than "no filter".
export const mapAssetFiltersToParams = (selectedFilters = {}) => {
    const params = {};

    const assetTypeCodes = selectedValues(selectedFilters, ASSET_FILTER_KEY_TYPE);
    if (assetTypeCodes.length) params.assetTypeCode = assetTypeCodes;

    // __unassigned__ is a legal value here and is NOT an assetState code:
    // asset_db turns it into currentStateCode IS NULL, OR-ed with whatever
    // real states are ticked beside it. Nothing in this mapping may validate
    // the code against the option list, and nothing may treat it as exclusive
    // of the others.
    const stateCodes = selectedValues(selectedFilters, ASSET_FILTER_KEY_STATE);
    if (stateCodes.length) params.stateCode = stateCodes;

    // Ids, not labels: the option list carries networkEntityMeta ids.
    const currentEntityIDs = selectedIDs(selectedFilters, ASSET_FILTER_KEY_ENTITY);
    if (currentEntityIDs.length) params.currentEntityID = currentEntityIDs;

    const vendorEntityIDs = selectedIDs(selectedFilters, ASSET_FILTER_KEY_VENDOR);
    if (vendorEntityIDs.length) params.sourceVendorEntityID = vendorEntityIDs;

    return params;
};

// The State filter's options, plus the one the option list cannot contain.
//
// stateCode's values are resolved from the assetState TABLE, so __unassigned__
// -- "registered outside a work order, so no lifecycle row and no state at
// all" -- is not among them. The list endpoint accepts it, so without this the
// panel could not express a narrowing the API supports, and those rows would
// be unreachable from the only control that filters by state.
//
// Appended rather than resolved server-side because it is not a row anywhere;
// asset_db holds the same constant on its side of the wire.
export const withUnassignedStateOption = (filters = {}) => {
    const stateFilter = filters?.[ASSET_FILTER_KEY_STATE];
    if (!stateFilter || !Array.isArray(stateFilter.values)) return filters || {};
    if (stateFilter.values.some((option) => option?.id === UNASSIGNED_STATE_KEY)) {
        return filters;
    }
    return {
        ...filters,
        [ASSET_FILTER_KEY_STATE]: {
            ...stateFilter,
            values: [
                ...stateFilter.values,
                { id: UNASSIGNED_STATE_KEY, value: UNASSIGNED_STATE_LABEL },
            ],
        },
    };
};

// Wraps a handler that narrows the list so the row selection is dropped first.
//
// EVERY narrowing needs this. Ticked rows are ids,
// and the export scopes the FILE by them (resourceIDs, narrowed server-side):
// tick three rows, type a search term, hit Download, and the ids name rows that
// are no longer in the result set -- so the user gets an empty CSV with nothing
// on screen to explain it. Select-all mode is dropped too: "all" meant all of
// the PREVIOUS query. Search, sort and the filter panel all go through here
// for that reason.
//
// Cleared rather than remapped: a tick means "this row", and there is no
// honest way to carry it across a query that may not return that row at all.
export const withSelectionCleared = (clearSelection, apply) => (...args) => {
    clearSelection();
    apply(...args);
};

// --------------------------------------------------------------------------
// Row actions
// --------------------------------------------------------------------------

// The actions the Actions column offers, given what the user holds.
//
// VIEW AND EDIT ARE GONE FROM THE ROW. View was a button that duplicated the
// serial cell -- the serial IS the row's identity and already opens the detail
// -- so it was a second control for the first thing anyone clicks. Edit moved
// into that detail view, where the asset being edited is on screen; a row-level
// Edit opens a dialog over a table, with nothing to check the change against.
//
// Delete is the only one left, and it is hidden rather than disabled: the
// backend re-checks it and answers 403 regardless, so this is presentation,
// not enforcement.
//
// MAY RETURN AN EMPTY LIST, and the caller must handle that by dropping the
// column. An Actions header over a column of blank cells is worse than no
// column -- it reads as a table that failed to load its buttons.
// NO ROW ACTIONS while the create flow is being finished. View, Edit and now
// Delete are all deferred, so this returns an empty list unconditionally and
// the caller drops the Actions column rather than rendering an empty one.
//
// Kept as a function rather than inlining []: the actions come back once the
// flow settles, and the caller already handles the empty case correctly. The
// permission is still granted and still enforced server-side -- what is gone
// is the control, not the right.
export const getAssetRowActions = () => [];

// --------------------------------------------------------------------------
// Selection -> a new work order
// --------------------------------------------------------------------------

// Ticked rows, as {id, serialNumber}.
//
// THE SERIAL IS CARRIED, NOT LOOKED UP LATER. Selection survives paging, and
// the loaded `records` only ever hold the page on screen -- so resolving ids
// back to serials at click time would silently drop everything ticked on a
// page the user has since left. Keeping both on the tick is the only shape
// that cannot lose one.
//
// CUSTODY IS CARRIED FOR THE SAME REASON. A work order moves assets FROM one
// place, so a selection spanning two holders is not a work order -- and the
// check has to hold across pages, where the row that set the source may no
// longer be loaded.
export const assetSelectionRow = (record) => ({
    id: record?.id,
    serialNumber: record?.serialNumber ?? "",
    currentEntityID: record?.currentEntityID ?? null,
    currentEntityName: record?.currentEntityName || "",
});

// --------------------------------------------------------------------------
// One source per work order
// --------------------------------------------------------------------------
//
// Every movement type takes ONE source, so a selection spanning two holders
// cannot become one work order. That rule has not changed. WHERE IT IS
// ENFORCED has.
//
// It used to refuse the TICK. That made the table argue with the user about a
// checkbox: ticking a row is not a claim that a work order is coming, it is
// how somebody reads, compares, downloads or mails a set. Refusing it took
// away four working features to protect one.
//
// So selection is free and the ACTION carries the rule. "+ New Work Order"
// goes disabled with the reason on hover, which puts the refusal on the
// control it actually applies to and leaves the rest of the toolbar alone.

// What the current selection is moving FROM, or null when nothing is ticked.
export const assetSelectionSource = (selectedRows = []) => {
    const rows = Array.isArray(selectedRows) ? selectedRows : [];
    if (!rows.length) return null;
    return {
        entityID: rows[0].currentEntityID ?? null,
        entityName: rows[0].currentEntityName || "",
    };
};

// The name to put in a sentence. "no recorded location" rather than a blank,
// because an empty phrase mid-sentence reads as a bug.
export const assetSourceName = (source) =>
    source?.entityName || "no recorded location";

// The DISTINCT places a selection spans, in the order they were ticked.
//
// NULL IS A PLACE. An asset with no custody has never been received anywhere
// -- the opening-balance case -- and it groups with other custody-less assets
// and with nothing else. Treating null as "matches anything" would let one
// into a batch with a real origin and be refused per-serial at preview.
export const assetSelectionSources = (selectedRows = []) => {
    const seen = new Map();
    (Array.isArray(selectedRows) ? selectedRows : []).forEach((row) => {
        const id = row?.currentEntityID ?? null;
        if (seen.has(id)) return;
        seen.set(id, row?.currentEntityName || "no recorded location");
    });
    return [...seen.entries()].map(([entityID, entityName]) => ({
        entityID,
        entityName,
    }));
};

/**
 * Can this selection become a work order? `{ blocked, reason }`.
 *
 * The reason NAMES THE PLACES, because "mixed custody" tells somebody they are
 * wrong without telling them what to change. With two it reads as a sentence;
 * past three the list stops being useful and the count does the work.
 */
export const selectionBlocksWorkOrder = (selectedRows = []) => {
    const rows = Array.isArray(selectedRows) ? selectedRows : [];
    if (!rows.length) {
        return { blocked: true, reason: "Select the assets to move first" };
    }
    const sources = assetSelectionSources(rows);
    if (sources.length <= 1) return { blocked: false, reason: "" };

    const names = sources.map((source) => source.entityName);
    const where =
        names.length <= 3
            ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
            : `${names.length} different places`;
    return {
        blocked: true,
        reason:
            `These assets are at ${where}. A work order moves assets from one `
            + "place, so raise one per place.",
    };
};

// The router state the "+ New Work Order" button travels with.
//
// STATE AND NOT THE QUERY STRING -- two hundred serials do not belong in a
// URL. The shape is read back by prefillSerialsFromState and
// resolveWorkOrderReturn, both of which tolerate it being absent entirely, so
// a direct visit or a refresh is a blank form with a Back button that still
// works.
//
// THE ORIGIN TRAVELS WITH THE SERIALS. Without it, backing out of the form
// landed on the Work Orders queue -- a different tab from the one the user was
// reading, with their selection gone. The LABEL goes too, because the tab's
// name is per-org config and the form has no pageMeta of its own to look it
// up in.
//
// Blank serials are dropped rather than sent as "": a row whose serial the API
// returned as null is a data fault, and putting an empty line in the box would
// make it look like the user's.
export const buildWorkOrderPrefillState = (
    selectedRows = [],
    { originTab = "", originLabel = "" } = {}
) => {
    // NOTE: the entry mode is not set here. A selection ALREADY answers the
    // one-or-several question, and resolveEntryMode reads it straight off the
    // serial count -- so carrying a second, redundant answer is a second thing
    // that could disagree with the first.
    const state = {
        serials: (Array.isArray(selectedRows) ? selectedRows : [])
            .map((row) => String(row?.serialNumber ?? "").trim())
            .filter(Boolean),
    };
    // Omitted rather than sent empty: resolveWorkOrderReturn reads the
    // PRESENCE of originTab as "the user came from somewhere", and "" would be
    // an origin that names nowhere.
    const slug = String(originTab || "").trim();
    if (!slug) return state;

    state.originTab = slug;
    const label = String(originLabel || "").trim();
    if (label) state.originLabel = label;
    return state;
};

// --------------------------------------------------------------------------
// "+ New Work Order" in select-all mode
// --------------------------------------------------------------------------
//
// With the header ticked the client holds one page of rows, not the selection,
// so the button FETCHES every matching asset -- same search, sort, filters and
// pins as the table (useAssets' requestArgs) -- and prefills the form with
// them, after the same one-source check a hand-picked selection gets.
//
// THE LIMIT IS CHECKED BEFORE ANY FETCH. A selection becomes the form's paste
// box, which takes at most MAX_ITEMS_PER_PASTE serials (50; the backend's
// MAX_ITEMS_PER_MOVEMENT). Fetching 1040 assets to then show a form that
// refuses them would be slow and pointless, so the button says so up front.

// GET /api/assets caps `limit` at 100 (ListAssetsSchema MAX_PAGE_LIMIT).
export const ASSET_LIST_MAX_PAGE_LIMIT = 100;

// Safety stop for the paging loop, far above any work order limit: a server
// that kept answering with rows would otherwise page forever.
const MAX_SELECT_ALL_PAGES = 50;

export const selectAllLimitMessage = (total, maxRows) =>
    `Select at most ${maxRows} assets or narrow the filters — ${total} match.`;

/**
 * Can select-all mode become a work order, BEFORE fetching? `{ blocked, reason }`.
 * The one-source check needs the rows, so it runs after the fetch.
 */
export const selectAllBlocksWorkOrder = (total, maxRows) => {
    const count = Number(total) || 0;
    if (count <= 0) {
        return { blocked: true, reason: "No assets match the current filters" };
    }
    if (Number.isFinite(maxRows) && count > maxRows) {
        return { blocked: true, reason: selectAllLimitMessage(count, maxRows) };
    }
    return { blocked: false, reason: "" };
};

/**
 * Every asset matching `requestArgs`, paged through `fetchPage` (getAssets).
 *
 * The API function is passed IN rather than imported so this stays a helper
 * the tests drive with a fake; the component passes the real getAssets from
 * src/Api/AssetApi.js.
 *
 * Re-checks the limit against the SERVER's total on the first page, because
 * the list may have grown since the count on screen was drawn.
 *
 * @returns {Promise<{ok: true, rows: Array} | {ok: false, reason: string}>}
 */
export const fetchAllAssetsForSelection = async ({
    fetchPage,
    requestArgs = {},
    maxRows,
    successCode = 200,
    pageSize = ASSET_LIST_MAX_PAGE_LIMIT,
}) => {
    const limit = Math.max(
        1,
        Math.min(
            ASSET_LIST_MAX_PAGE_LIMIT,
            Number.isFinite(maxRows) && maxRows > 0 ? maxRows : pageSize,
            pageSize
        )
    );
    const byID = new Map();
    let serverTotal = null;

    for (let page = 1; page <= MAX_SELECT_ALL_PAGES; page += 1) {
        // Sequential on purpose: each page is checked before the next is
        // requested, and the limit means this is one request in practice.
        const res = await fetchPage({ ...requestArgs, page, limit });
        if (res?.responseStatus?.code !== successCode) {
            return {
                ok: false,
                reason: res?.responseStatus?.msg || "Could not load the selected assets",
            };
        }
        const total = Number(res?.data?.pagination?.total);
        if (serverTotal === null && Number.isFinite(total)) {
            serverTotal = total;
            if (Number.isFinite(maxRows) && total > maxRows) {
                return { ok: false, reason: selectAllLimitMessage(total, maxRows) };
            }
        }
        const records = Array.isArray(res?.data?.records) ? res.data.records : [];
        records.forEach((record) => {
            if (record?.id !== null && record?.id !== undefined && !byID.has(record.id)) {
                byID.set(record.id, record);
            }
        });
        if (Number.isFinite(maxRows) && byID.size > maxRows) {
            return { ok: false, reason: selectAllLimitMessage(byID.size, maxRows) };
        }
        const done =
            records.length < limit ||
            (serverTotal !== null && byID.size >= serverTotal);
        if (done) break;
    }

    return { ok: true, rows: [...byID.values()] };
};

/**
 * Select-all -> the router state for the work order form, or the reason it
 * cannot be one. Same limit, same one-source rule, same prefill shape as a
 * hand-picked selection.
 *
 * @returns {Promise<{ok: true, state: Object} | {ok: false, reason: string}>}
 */
export const prepareSelectAllWorkOrder = async ({
    fetchPage,
    requestArgs = {},
    total,
    maxRows,
    successCode = 200,
    originTab = "",
    originLabel = "",
}) => {
    const precheck = selectAllBlocksWorkOrder(total, maxRows);
    if (precheck.blocked) return { ok: false, reason: precheck.reason };

    const fetched = await fetchAllAssetsForSelection({
        fetchPage,
        requestArgs,
        maxRows,
        successCode,
    });
    if (!fetched.ok) return fetched;
    if (!fetched.rows.length) {
        return { ok: false, reason: "No assets match the current filters" };
    }

    const rows = fetched.rows.map(assetSelectionRow);
    const block = selectionBlocksWorkOrder(rows);
    if (block.blocked) return { ok: false, reason: block.reason };

    return {
        ok: true,
        state: buildWorkOrderPrefillState(rows, { originTab, originLabel }),
    };
};

// --------------------------------------------------------------------------
// Cell formatting
// --------------------------------------------------------------------------
//
// These two formats are NOT a style choice. They reproduce what the server
// writes into the CSV:
//
//   datetime -> localised to the org's timezone, then
//               strftime("%d %b %Y %I:%M %p")
//               (app/util/csv_helpers.py::convert_non_serializable)
//   date     -> strftime("%d %b %Y"), applied in the module's OWN serialiser
//               (app/services/asset.py::_serialiseExportRow) rather than in
//               that shared helper, whose date branch every other export in
//               the product already depends on
//
// The file is built server-side, so the cell has to match the file rather than
// the other way round. Changing either string means changing it there too, and
// the parity test asserts both against literal expected output.

export const DEFAULT_DATE_FORMAT = "DD MMM YYYY";
export const DEFAULT_DATE_TIME_FORMAT = "DD MMM YYYY hh:mm A";

const blank = (value) =>
    value === null || value === undefined || value === "";

// A DATE column: manufacturingDate is a bare YYYY-MM-DD with no timezone, so
// it is formatted where it stands. Running it through the org-timezone
// converter would parse it as UTC midnight and show the previous day for every
// org west of UTC -- and the server, which does not convert it either, would
// then disagree with the screen.
export const formatAssetDate = (value, fallback = "-") => {
    if (blank(value)) return fallback;
    const formatted = changeDateFormat(value, DEFAULT_DATE_FORMAT);
    return !formatted || formatted === "Invalid date" ? fallback : formatted;
};

// A DATETIME column: createdAt / updatedAt / effectiveFrom are UTC instants,
// so they go through the org-timezone helper every other table uses -- the
// same conversion the server applies with getTimezone(orgID, userID).
export const formatAssetDateTime = (value, fallback = "-") => {
    if (blank(value) || value === "0000-00-00 00:00:00") return fallback;
    const formatted = getMetricTimeStamp(value, DEFAULT_DATE_TIME_FORMAT);
    return !formatted || formatted === "Invalid date" ? fallback : formatted;
};

// One cell of the TABLE. Every custom renderer in AssetsTabContent takes its
// text from here, so "what the screen shows" has a single definition that a
// test can hold against the server's.
//
// It is deliberately NOT on the download path any more. The server returns
// rows that are already renamed and already localised; re-running this over
// them would parse "04 Mar 2025 03:41 PM" as "YYYY-MM-DD HH:mm:ss", fail, and
// blank the column.
export const formatAssetCell = (column, record) => {
    const type = column?.valueRenderingType;
    const raw = record?.[column?.dataIndex];
    const fallback = column?.defaultValue ?? "";

    if (type === "date") return formatAssetDate(raw, fallback);
    if (type === "date-time") return formatAssetDateTime(raw, fallback);

    // The chip's colour is styling, not data: the file carries the label the
    // chip shows. text-with-copy likewise -- the copy button is not a value.
    if (type === "status-chip" || type === "text-with-copy") {
        return blank(raw) ? fallback : raw;
    }

    // Everything the two tabs share -- timestamp, number, the text formats and
    // the blank/defaultValue rules -- stays in the one implementation.
    return formatCellForCSV(column, record);
};

// --------------------------------------------------------------------------
// CSV
// --------------------------------------------------------------------------

// `title` is only a fallback where it is still a string: getColumnsConfig
// replaces it with a render function, and a function used as a header label
// becomes an empty CSV column rather than an error.
function csvHeaderLabel(column) {
    if (typeof column?.headerLabel === "string") return column.headerLabel;
    if (typeof column?.title === "string") return column.title;
    return column?.dataIndex;
}

// The `columns` payload: what the server renames each field to.
//
// ONE column list for both actions -- the server builds the download and the
// mail attachment from one frame, and sending the same list for each is what
// makes them the same file rather than merely similar.
//
// There used to be an id sentinel ("__assetID") on the download so the client
// could scope the file to the ticked rows. Selection now travels to the server
// as resourceIDs / selectAll (the Inventory contract), so the server is the
// single place the file is narrowed and the sentinel is gone.
export const buildAssetCSVColumnsPayload = (columns = []) =>
    (Array.isArray(columns) ? columns : [])
        .filter((column) => column?.dataIndex && !isActionColumn(column))
        .map((column) => ({
            dataIndex: column.dataIndex,
            headerLabel: csvHeaderLabel(column),
        }));

// The server's rows -> the rows the download writes.
//
// The rows arrive ALREADY keyed by header label, ALREADY localised and ALREADY
// scoped to the selection (resourceIDs / selectAll), so this neither formats
// nor filters. What is left is genuinely client-side:
//
//   1. The column's placeholder. The server emits null for an empty cell and
//      onDownloadCSV writes "" for it, while the table shows the column's
//      defaultValue -- so an asset with no custody would read "-" on screen and
//      blank in the file.
//   2. Rebuilding each row from `columns`, which drops any field the server
//      returned that the user has hidden.
export const buildAssetCSVRows = (records = [], columns = []) => {
    const exportable = (Array.isArray(columns) ? columns : []).filter(
        (column) => column && !isActionColumn(column) && column.dataIndex
    );

    return (Array.isArray(records) ? records : []).map((record) =>
        Object.fromEntries(
            exportable.map((column) => {
                const key = csvHeaderLabel(column);
                const value = record?.[key];
                return [key, blank(value) ? column?.defaultValue ?? "" : value];
            })
        )
    );
};

// Which columns start visible. Delegates the defaultVisible / dataIndex rules
// to the shared helper and then drops the Actions column, which that helper
// only knows to exclude under its Network spelling.
export const getAssetDefaultSelectedColumns = (columnsConfig, columnOrdering) => {
    const actionIndexes = new Set(
        Object.values(columnsConfig || {})
            .filter(isActionColumn)
            .map((config) => config?.dataIndex)
            .filter(Boolean)
    );
    return getDefaultSelectedColumns(columnsConfig, columnOrdering).filter(
        (dataIndex) => !actionIndexes.has(dataIndex)
    );
};

// --------------------------------------------------------------------------
// Detail view
// --------------------------------------------------------------------------

// The asset's OWN fields, for the header card of the detail page.
//
// currentStateLabel and currentEntityName are shown here because
// they are what the asset record says about itself. They are a CACHE of the
// lifecycle, maintained by the work order flow -- they are read, never edited,
// and the timeline below the card is rendered from the lifecycle endpoint, not
// from these.
export const getAssetDetailFields = (asset) => {
    const record = asset || {};
    return [
        { key: "serialNumber", label: "Serial Number", value: record.serialNumber || "-" },
        { key: "assetTypeCode", label: "Asset Type", value: record.assetTypeCode || "-" },
        {
            key: "currentStateLabel",
            label: "Current State",
            value: record.currentStateLabel || "-",
            badgeColorTheme: record.currentStateBadgeColorTheme,
            isState: true,
        },
        {
            key: "currentEntityName",
            // Not "Custody", which is the data model's word rather than a
            // reader's, and not "Route" either -- this is one asset's current
            // holder, not a FROM -> TO pair.
            label: "Currently Held By",
            value: record.currentEntityName || "-",
        },
        {
            key: "currentLoanReferenceID",
            label: "Loan ID",
            value: record.currentLoanReferenceID || "-",
        },
        {
            key: "manufacturingDate",
            label: "Manufactured On",
            value: formatAssetDate(record.manufacturingDate),
        },
        {
            key: "createdAt",
            label: "Registered On",
            value: formatAssetDateTime(record.createdAt),
        },
        {
            key: "updatedAt",
            label: "Last Updated",
            value: formatAssetDateTime(record.updatedAt),
        },
    ];
};

// assetLifecycleRecord rows -> timeline rows.
//
// Read from the /lifecycle endpoint ONLY. The newest row here and the asset's
// cached currentStateCode are different data with different guarantees, and
// rendering either from the other would hide exactly the drift this timeline
// exists to expose.
export const buildLifecycleRows = (records = []) =>
    (Array.isArray(records) ? records : []).map((record, index) => ({
        key: record?.id ?? `lifecycle-${index}`,
        stateCode: record?.stateCode || null,
        stateLabel: record?.stateLabel || record?.stateCode || "-",
        theme: getBadgeTheme(record?.stateBadgeColorTheme),
        entityName: record?.entityName || "-",
        effectiveFrom: formatAssetDateTime(record?.effectiveFrom),
        recordedAt: formatAssetDateTime(record?.createdAt),
    }));

// --------------------------------------------------------------------------
// Form validation -- the client half of the backend's rules
// --------------------------------------------------------------------------
//
// A mirror for the user's benefit: inline errors instead of a round trip. It
// is never the authority -- app/services/asset.py re-validates everything and
// its per-field 400 is painted onto the form the same way -- so a rule missing
// here still surfaces correctly, just later.

export const MAX_SERIAL_LENGTH = 255;
export const MAX_LOAN_REFERENCE_LENGTH = 64;
export const MAX_DELETE_REASON_LENGTH = 512;

const trimmed = (value) => String(value ?? "").trim();

export const validateAssetForm = (values = {}) => {
    const errors = {};

    const serialNumber = trimmed(values.serialNumber);
    if (!serialNumber) {
        errors.serialNumber = "Serial number is required";
    } else if (serialNumber.length > MAX_SERIAL_LENGTH) {
        errors.serialNumber = `Serial number must be at most ${MAX_SERIAL_LENGTH} characters`;
    }

    const loanReference = trimmed(values.currentLoanReferenceID);
    if (loanReference.length > MAX_LOAN_REFERENCE_LENGTH) {
        errors.currentLoanReferenceID = `Loan ID must be at most ${MAX_LOAN_REFERENCE_LENGTH} characters`;
    }

    // The picker can only produce this shape, but the value is also SEEDED
    // from the API on edit, so a malformed row still has to be caught rather
    // than posted back unchanged.
    const manufacturingDate = trimmed(values.manufacturingDate);
    if (manufacturingDate && !/^\d{4}-\d{2}-\d{2}$/.test(manufacturingDate)) {
        errors.manufacturingDate = "Enter a valid date";
    }

    return errors;
};

// The date picker's value -> the string the form and the payload carry.
//
// SingleDatePicker hands back a dayjs object (or null on clear) and the API
// takes a plain YYYY-MM-DD date, so this is the one boundary between the two.
// "" rather than null for a cleared field, because that is what the rest of
// the form holds for "empty" and buildAssetPayload is the single place that
// turns empty into the null the column wants.
export const assetDateFromPicker = (date) =>
    date && typeof date.format === "function" ? date.format("YYYY-MM-DD") : "";

// The payload for create / update.
//
// currentStateCode, currentEntityID and receiptCount are absent BY
// CONSTRUCTION, not by being filtered out: they are a cache of the lifecycle
// and the request schemas refuse them outright.
//
// assetTypeCode is absent DELIBERATELY, on create as well as on edit. The
// platform has exactly one asset type and the server now decides it:
// assetTypeCode is optional on POST /api/assets (load_default=None) and
// createAsset fills in DEFAULT_ASSET_TYPE_CODE from asset_db.py when it is
// omitted. Sending a literal from here would put a second definition of that
// constant in JS, which is the drift the backend change removed.
//
// An emptied optional field is sent as null, not "": the columns are nullable
// and an empty string would store a value that prints as blank but is not.
export const buildAssetPayload = (values = {}) => {
    return {
        serialNumber: trimmed(values.serialNumber),
        currentLoanReferenceID: trimmed(values.currentLoanReferenceID) || null,
        manufacturingDate: trimmed(values.manufacturingDate) || null,
        sourceVendorEntityID:
            values.sourceVendorEntityID === "" ||
            values.sourceVendorEntityID === null ||
            values.sourceVendorEntityID === undefined
                ? null
                : Number(values.sourceVendorEntityID),
    };
};

// The delete reason. Required and non-blank: the backend strips and re-checks
// it, and a soft-deleted serial is never recycled, so this is the only thing
// that will ever explain the disappearance. Validated here so a blank reason
// is an inline message rather than a 400.
export const validateDeleteReason = (reason) => {
    const value = trimmed(reason);
    if (!value) return "Tell us why this asset is being removed";
    if (value.length > MAX_DELETE_REASON_LENGTH) {
        return `Reason must be at most ${MAX_DELETE_REASON_LENGTH} characters`;
    }
    return "";
};

// The API's data.errors -> { [field]: message }, so a server rejection lands on
// the form identically to a local one.
export const mapAssetServerErrors = (response) => {
    const errors = {};
    (response?.data?.errors || []).forEach(({ field, message }) => {
        if (field) errors[field] = message;
    });
    return errors;
};

// Where a Current Custody cell goes, or null for "render it as plain text".
//
// TWO ways a name can be unlinkable, and they are not the same thing. An asset
// nobody is holding has no entity at all -- the cell is the column's
// defaultValue, a dash, and a dash that navigates is a trap. An asset held by
// someone the API named but did not give an id for is a data gap, and a link
// to /network/undefined turns that gap into an error page. Both end up as
// text, so the caller has one question to ask.
//
// The id FIELD is config, not a constant: point entityIDDataIndex at another
// column's id and any name becomes a link to that entity.
export const assetCustodyLinkTarget = (record, config) => {
    const entityID = record?.[config?.entityIDDataIndex || "currentEntityID"];
    if (entityID === null || entityID === undefined || entityID === "") return null;
    const name = record?.[config?.dataIndex];
    if (name === null || name === undefined || name === "") return null;
    return `/asset-management/network/${entityID}`;
};

// "This asset has a work order waiting on a checker", or null.
//
// A MARKER BESIDE THE STATE, not a change to it. The state column says where
// the asset IS; a pending work order says something is proposed about it, and
// the two must not be merged -- an asset held by a warehouse with a dispatch
// awaiting approval is still, right now, in that warehouse. Writing "Pending"
// into the state would make the list disagree with the asset's own history.
//
// It is also why this does NOT go through formatAssetCell and so never reaches
// the CSV: the column's value is unchanged, and the file and the screen still
// carry the same state. The badge is additional, not a transform.
//
// pendingWorkOrderCount comes from the list endpoint, counted per asset over
// work orders still awaiting review.
export const pendingWorkOrderNote = (record) => {
    const count = Number(record?.pendingWorkOrderCount);
    if (!Number.isFinite(count) || count <= 0) return null;
    return {
        count,
        // The number is on the badge; the tooltip says what it counts, because
        // a bare "2" beside a state chip is an unexplained number.
        tooltip: count === 1
            ? "1 work order is waiting for approval"
            : `${count} work orders are waiting for approval`,
    };
};
